/* Dynamic Plot code copyright 2015-2025 (c) Tuva Labs Inc. */

export const wrapDynSquaresOnLineModel = function(Dyn) {
    class SquaresOnLineModel {
        /**
         * A utility model of the data needed to show squares on a single line.  Its job is to add or remove
         * visible squares to a single Movable Line, Least Squares Line, or Model Function curve ("Show Squares" or "Hide Squares").
         * Squares are added and removed from the given "squaresSet", part of the Adornments for the plot element.
         * Note that each constructed SquaresOnLineModel has the custom data for that particular line,
         * then for updateSquaress we pass in the common parameters common to all lines.
         */
        constructor( xAxisView, yAxisView, xAttrIndex, yAttrIndex, lineEquation, filterParams, squaresSet, color ) {
            this.xAxisView = xAxisView;
            this.yAxisView = yAxisView.getSubviewForActiveAttribute( yAttrIndex );
            this.xAttrIndex = xAttrIndex; // reference to x-axis attribute for line
            this.yAttrIndex = yAxisView.getSubviewIndexForActiveAttribute( yAttrIndex ); // reference to y-axis attribute for line
            this.lineEquation = lineEquation; // { fx:..., optional slope:..., intercept:... }
            Dyn.assert( typeof lineEquation.fx === "function", "undefined line equation" );
            this.filterParams = filterParams; // data used by filterFunction to select cases for this line
            this.squaresSet = squaresSet; // Raphael set() added to Adornments for the given plot element
            this.color = color; // color of squares
            Dyn.assert( this.xAxisView.getActiveAttribute( this.xAttrIndex));
            Dyn.assert( this.yAxisView.getActiveAttribute( this.yAttrIndex));
        }

        /** Get Y-axis value of line, y=f(x) a.k.a y=mx+b */

        /**
         * Compute the width of the square from the height, in screen coordinates.
         * Note that both values can be positive or negative, determining the placement
         * of the square, so that the square does not intersect the line.
         * negative height = data point is below line, positive=above line.
         * negative width = squares goes to left of data point, positive=to right.
         * The square should be places so it does not intersect with the line.
         * @param isPositiveSlope {boolean} -- constant from isPositiveSlopeOnScreen()
         * @param height {number} -- positive or negative distance from line to point
         */
        widthOfSquareFromHeight( isPositiveSlope, height ) {
            let isPositiveHeight = (height > 0);
            return (isPositiveSlope==isPositiveHeight) ? Math.abs(height) : -Math.abs(height);
        }
        /** is movable/least-squares line slope positive *in screen coordinates*? */
        isPositiveSlopeOnScreen() {
            return this.yAxisView.dataToPixel( this.lineEquation.fx(0)) < this.yAxisView.dataToPixel( this.lineEquation.fx(1));
        }

        /** add or remove visible squares to a least squares line.
         * Our constructor has the unique paramaters for this line, we pass in parameters in common to multiple lines.
         * @param showSquares {boolean} -- show or hide the squares?
         * @param filterFunction {*} -- which cases are on this line (e.g. for least squares line by category)
         * @param plotElement {Dyn.PlotElement} -- which plot element (Dyn.LeastSquaresElement, Dyn.DiagRefLineElementY2, etc.)
         * @param lineElement {Raphael path} -- visible SVG path for movable line/least squares line
         */
        updateSquares( showSquares, filterFunction, plotElement, lineElement ) {
            if( ! showSquares ) {
                SquaresOnLineModel.removeSquares( this.squaresSet );
            } else {
                this.updateDashedLine();
                const clipRectString = Dyn.plotView.getPlotInteriorClipRect(),
                    xCases = this.xAxisView.getActiveAttribute( this.xAttrIndex).cases,
                    yCases = this.yAxisView.getActiveAttribute( this.yAttrIndex).cases,
                    numCases = Math.min( xCases.length, yCases.length ),
                    isPositiveSlope = this.isPositiveSlopeOnScreen();
                let set = this.squaresSet,
                    r = new Dyn.mathUtil.Rect(),
                    newNumSquares = 0;
                for (let c = 0; c < numCases; c++) {
                    let x = xCases[c],
                        y = yCases[c];
                    // skipping non-numeric and missing and filtered cases
                    if( filterFunction( this.filterParams, c, x, y )) {
                        // get corners of square in view coordinates
                        r.x = this.xAxisView.dataToPixel( x );
                        r.y = this.yAxisView.dataToPixel( y );
                        let y2 = this.lineEquation.fx(x);
                        if( Dyn.mathUtil.isFinite( y2 )) { // skip points where line is undefined
                            r.height = this.yAxisView.dataToPixel( y2 ) - r.y;
                            r.width = this.widthOfSquareFromHeight( isPositiveSlope, r.height );
                            r.normalize(); // adjust rect so height and width are positive values
                            // add or update visible rectangle to match
                            let squareEl = set.getNthSquare( newNumSquares );
                            if( squareEl ) {
                                squareEl.attr( { x: r.x, y: r.y, width: r.width, height: r.height } );
                            } else {
                                squareEl = set.paper.rect( r.x, r.y, r.width, r.height )
                                    .hover( hoverIn, hoverOut )
                                    .data( "caseIndex", c )
                                    .data( "attrIndex", Math.max( this.xAttrIndex, this.yAttrIndex ) );
                                //.data( "tooltip", Dyn.loc("Case: {{n}}", {n:c+1}));
                                set.push( squareEl );
                            }
                            squareEl.attr( {
                                stroke: this.color,
                                'stroke-width': Dyn.plotUtil.showSquaresLineWidth,
                                'clip-rect': clipRectString
                            } );
                            ++newNumSquares;
                        }
                    }
                }
                // remove unused squares
                while( set.getNumSquares() > newNumSquares ) {
                    set.pop().remove();
                }
                Dyn.assert( set.getNumSquares() === newNumSquares, "Least Squares number mismatch" );
            }

            function hoverIn( e ) {
                let squareElement = this,
                    lineColor = lineElement.attr( "stroke" ),
                    caseIndex = squareElement.data("caseIndex"),
                    attrIndex = squareElement.data("attrIndex"),
                    caseIcon = Dyn.plotView.getCaseIconForGlow( caseIndex, attrIndex );
                // if( Dyn.toolTip.isMyElement( squareElement ) ){
                //     Dyn.toolTip.update( squareElement, e, {} );
                // } else {
                //     Dyn.toolTip.show( squareElement, e, {} );
                // }
                if( caseIcon ) {
                    caseIcon.toFront();
                    plotElement.addGlow( [caseIcon, lineElement, squareElement], lineColor, Dyn.plotUtil.glowWidth );
                } else {
                    plotElement.addGlow( [lineElement, squareElement], lineColor, Dyn.plotUtil.glowWidth );
                }
            }

            function hoverOut( /* e  */ ) {
                let squareElement = this;
                // if( Dyn.toolTip.isMyElement( squareElement ) ){ // tip element from hoverIn()
                //     Dyn.toolTip.hide();
                // }
                plotElement.removeGlow();
                plotElement.plotShaper.layerAdornments(); // undo layering from hoverIn()
            }
        }

        /**
         * If needed, add a dashed line which extends the visible line we are showing squares on,
         * so that the line is visible for every X value in the plotting area.
         * Requires that this.squaresSet.getDashedLine() be defined to return the dashed line element, if any,
         * or undefined if no dashed line is needed.
         * This is true for movable lines, which are line segments, but not for least squares or model function lines.
         * See also removeSquares() which removes the dashed line if it exists.
         */
        updateDashedLine() {
            let set = this.squaresSet; // Raphael set that holds squares and related visible elements
            if( set.getDashedLine ) {
                Dyn.assertFiniteNumeric( this.lineEquation.slope, "slope parameter required for dashed line" );
                Dyn.assertFiniteNumeric( this.lineEquation.intercept, "intercept parameter required for dashed line" );
                let intercepts = Dyn.plotUtil.lineToAxisIntercepts( this.lineEquation.slope, this.lineEquation.intercept,
                        this.xAxisView, this.yAxisView );
                Dyn.assertFiniteNumeric( intercepts.pt1.x );
                Dyn.assertFiniteNumeric( intercepts.pt1.y );
                Dyn.assertFiniteNumeric( intercepts.pt2.x );
                Dyn.assertFiniteNumeric( intercepts.pt2.y );
                Dyn.plotUtil.addAxisInterceptsViewPts( intercepts, this.xAxisView, this.yAxisView ); // add view coordinates
                let pathString = "M" + intercepts.viewPt1.x.toFixed( 1 ) + "," + intercepts.viewPt1.y.toFixed( 1 ) +
                        "L" + intercepts.viewPt2.x.toFixed( 1 ) + "," + intercepts.viewPt2.y.toFixed( 1 );
                // remove optional dashed line, which extends Movable Line to edges of plotting area
                let dashedLine = set.getDashedLine();
                if( dashedLine ) {
                    dashedLine.attr({ path:pathString, stroke:this.color });
                } else {
                    dashedLine = set.paper.path( pathString ).attr( 'stroke', this.color );
                    set.push( dashedLine );
                    Dyn.assert( dashedLine === set.getDashedLine(), "Dashed line pushed to set at wrong index, index="+(set.length-1));
                }
                dashedLine.node.setAttribute('stroke-dasharray', Dyn.plotUtil.showSquaresDashArray );
            }
        }

        /** remove squares added with updateSquares() */
        static removeSquares( squaresSet ) {
            if( squaresSet.getDashedLine ) {
                // remove optional dashed line, which extends Movable Line to edges of plotting area
                let dashedLine = squaresSet.getDashedLine();
                dashedLine && dashedLine.attr({ path:"m0,0" });
            }
            while( squaresSet.getNumSquares() > 0 ) {
                squaresSet.pop().remove(); // remove visible Raphael rect
                // Warning: pop() reduces .length but element not immediately removed from array by Raphaeljs
            }
            Dyn.assert( squaresSet.getNumSquares() === 0 );
        }

        static xyNumericFilter() {
            // filter function to select cases with numeric X and Y values, ignoring any other attributes (e.g. 2nd numeric attribute, categorical attribute, etc).
            return {
                params: { dataSet:Dyn.dataSet },
                caseFilter: function xyNumericFilter( params, caseIndex, xValue, yValue ) {
                    // return true if case at caseIndex should be included in show squares
                    return( params.dataSet.isCaseNonMissingNonFiltered( caseIndex ) && // exclude filtered and missing cases (determined by dot plot)
                        Dyn.mathUtil.isFinite( xValue ) &&  // exclude non-numeric X values
                        Dyn.mathUtil.isFinite( yValue )     // exclude non-numeric Y values
                    );
                }
            }
        }
    } // end Dyn.SquaresOnLineModel
    Dyn.SquaresOnLineModel = SquaresOnLineModel;

} // end wrapDynSquaresOnLineModel
